import { useState, useEffect, useMemo, useRef } from 'react';
import { createClient } from '@supabase/supabase-js';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Users2,
  BarChart2,
  CalendarIcon,
  Clock3,
  X,
  CheckCircle2,
  Plus,
  Tag,
  FolderPlus,
  Folder,
  ArrowLeft,
  Search,
  Filter,
  Tags,
  Edit2,
  Settings,
  Shield,
  User
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { notifyProjectCreation, notifySubProjectCreation } from '../../lib/notificationService';

// Supabase ayarları
const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY
);

const Projects = () => {
  // Navigation hook
  const navigate = useNavigate();
  
  // State tanımlamaları
  const [projects, setProjects] = useState([]);
  const [subProjects, setSubProjects] = useState({});
  const [selectedProjectType, setSelectedProjectType] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loadingStates, setLoadingStates] = useState({
    projects: false,
    subProjects: false,
    creation: false,
    categories: false
  });
  const [projectCategories, setProjectCategories] = useState([]);
  const [selectedSubProjects, setSelectedSubProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isNewCategoryModalOpen, setIsNewCategoryModalOpen] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', color: 'blue' });
  const [editingCategory, setEditingCategory] = useState(null);

  // Modal kontrolleri için state'ler
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [selectedProjectForModal, setSelectedProjectForModal] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isSubProjectModalOpen, setIsSubProjectModalOpen] = useState(false);
  const [showAllSubProjects, setShowAllSubProjects] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [projectToDelete, setProjectToDelete] = useState(null);

  // Yeni proje form state'i
  const [newProject, setNewProject] = useState({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    status: 'active',
    task_count: 0,
    team_size: 0,
    completed_tasks: 0,
    categories: [],
    sub_projects: [],
    has_end_date: true
  });

  // Yeni alt proje form state'i
  const [newSubProject, setNewSubProject] = useState({
    name: '',
    description: '',
    parent_project_id: null,
    start_date: '',
    end_date: '',
    has_end_date: false
  });

  // Tarih değişiklikleri için özel bir işleyici
  const handleDateChange = (e) => {
    const { name, value } = e.target;
    
    setNewSubProject(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Sadece kullanıcıyı uyar, değeri sıfırlama
    if (name === 'end_date' && newSubProject.start_date && value && value < newSubProject.start_date) {
      // toast.warning('Bitiş tarihi başlangıç tarihinden önce olamaz');
    }
  };

  // Alt projeleri filtrele ve sırala
  const getFilteredAndSortedSubProjects = (subProjects, search, sort) => {
    let filtered = subProjects;
    
    // Arama filtrelemesi
    if (search) {
      filtered = subProjects.filter(sp => 
        sp.name.toLowerCase().includes(search.toLowerCase()) ||
        sp.description?.toLowerCase().includes(search.toLowerCase())
      );
    }
    
    // Sıralama
    return filtered.sort((a, b) => {
      switch (sort) {
        case 'name-asc':
          return a.name.localeCompare(b.name);
        case 'name-desc':
          return b.name.localeCompare(a.name);
        case 'status':
          return a.status.localeCompare(b.status);
        case 'team-size':
          return (b.team_size || 0) - (a.team_size || 0);
        default:
          return 0;
      }
    });
  };

  // Projeleri çek
  const fetchProjects = async () => {
    try {
      setLoadingStates(prev => ({ ...prev, projects: true }));
      
      // Önce "Projeler" ana kategorisinin ID'sini bul
      const { data: parentProjects, error: parentError } = await supabase
        .from('projects')
        .select('id')
        .eq('name', 'Projeler')
        .single();

      if (parentError) {
        console.error('Ana proje bulunamadı:', parentError);
        toast.error('Ana proje bulunamadı');
        setProjects([]);
        return;
      }

      // Sadece "Projeler" kategorisine ait alt projeleri getir
      let query = supabase
        .from('sub_projects')
        .select('*')
        .eq('parent_project_id', parentProjects.id)
        .neq('status', 'deleted'); // Silinen projeleri hariç tut

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Alt projeler alınırken hata oluştu:', error);
        toast.error('Alt projeler alınırken bir hata oluştu');
        return;
      }

      // Arama filtrelemesi
      const filteredData = searchQuery
        ? data.filter(project =>
            project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            project.description?.toLowerCase().includes(searchQuery.toLowerCase())
          )
        : data;

      setProjects(filteredData || []);
    } catch (err) {
      console.error('Alt projeler alınırken hata oluştu:', err);
      toast.error('Alt projeler alınırken bir hata oluştu');
    } finally {
      setLoadingStates(prev => ({ ...prev, projects: false }));
    }
  };

  // Proje kategorilerini çek
  const fetchProjectCategories = async (projectId) => {
    try {
      const { data, error } = await supabase
        .from('project_categories')
        .select(`
          *,
          category:categories(*)
        `)
        .eq('project_id', projectId);

      if (error) {
        console.error('Kategoriler alınırken hata oluştu:', error);
        return;
      }

      setProjectCategories(data || []);
    } catch (err) {
      console.error('Kategoriler alınırken hata oluştu:', err);
    }
  };

  // Alt projeleri çek
  const fetchSubProjects = async (projectId) => {
    try {
      setLoadingStates(prev => ({ ...prev, subProjects: true }));
      // console.log(`Fetching sub-projects for project ID: ${projectId}`);
      
      const { data, error } = await supabase
        .from('sub_projects')
        .select('*')
        .eq('parent_project_id', projectId);

      if (error) {
        console.error('Alt projeler alınırken hata oluştu:', error);
        return;
      }

      // console.log(`Found ${data?.length || 0} sub-projects for project ${projectId}:`, data);

      setSubProjects((prev) => ({
        ...prev,
        [projectId]: data || [],
      }));
    } catch (err) {
      console.error('Alt projeler alınırken hata oluştu:', err);
    } finally {
      setLoadingStates(prev => ({ ...prev, subProjects: false }));
    }
  };

  // Proje kartına tıklanınca modal aç ve ilgili alt projeleri çek
  const handleProjectClick = async (subProject) => {
    try {
      setSelectedProjectForModal(subProject);
      setIsProjectModalOpen(true);
      setShowAllSubProjects(false);
      setLoadingStates(prev => ({ ...prev, subProjects: true }));
      
      // console.log(`Loading details for sub-project ID: ${subProject.id}`);
      
      // Bu alt proje için gerçek istatistikleri hesapla
      const [tasksResult, membersResult] = await Promise.all([
        // Bu alt projeye ait görevleri getir
        supabase
          .from('tasks')
          .select('id, status, assigned_members')
          .eq('sub_project_id', subProject.id),
        
        // Bu alt projeye atanmış kullanıcıları getir (görevlerden)
        supabase
          .from('tasks')
          .select('assigned_members')
          .eq('sub_project_id', subProject.id)
          .not('assigned_members', 'is', null)
      ]);

      if (tasksResult.error) {
        console.error('Görevler alınırken hata:', tasksResult.error);
      }

      if (membersResult.error) {
        console.error('Üye bilgileri alınırken hata:', membersResult.error);
      }

      const tasks = tasksResult.data || [];
      const memberTasks = membersResult.data || [];

      // Görev sayısını hesapla
      const taskCount = tasks.length;

      // Tamamlanan görev sayısını hesapla
      const completedTasks = tasks.filter(task => task.status === 'done').length;

      // Ekip üyesi sayısını hesapla (benzersiz kullanıcılar)
      const allMembers = new Set();
      memberTasks.forEach(task => {
        let assignedMembers = task.assigned_members;
        
        // Eğer string olarak saklanmışsa parse et
        if (typeof assignedMembers === 'string') {
          try {
            assignedMembers = JSON.parse(assignedMembers);
          } catch (e) {
            // console.warn('JSON parse hatası:', e);
            return;
          }
        }
        
        if (assignedMembers && Array.isArray(assignedMembers)) {
          assignedMembers.forEach(member => {
            if (member && (member.id || member.user_id)) {
              allMembers.add(member.id || member.user_id);
            }
          });
        }
      });
      const teamSize = allMembers.size;

      // Güncellenmiş istatistikleri projekte sakla
      const updatedSubProject = {
        ...subProject,
        task_count: taskCount,
        completed_tasks: completedTasks,
        team_size: teamSize
      };

      setSelectedProjectForModal(updatedSubProject);
      setSubProjects(prev => ({
        ...prev,
        [subProject.id]: [] // Alt projenin alt projeleri yoksa boş array
      }));
      setSelectedSubProjects([]);
      setProjectCategories([]);

      // console.log(`İstatistikler güncellendi: Görev: ${taskCount}, Tamamlanan: ${completedTasks}, Ekip: ${teamSize}`);

    } catch (err) {
      console.error('Alt proje detayları alınırken hata:', err);
      toast.error('Alt proje detayları alınırken bir hata oluştu');
    } finally {
      setLoadingStates(prev => ({ ...prev, subProjects: false }));
    }
  };

  // Alt projeleri çek ve seçili olarak ayarla
  const handleSubProjectModalOpen = async (project) => {
    try {
      setLoadingStates(prev => ({ ...prev, subProjects: true }));
      // console.log(`Opening sub-project manager for project ID: ${project.id}`);
      
      // Tüm alt projeleri çek
      const { data: allSubProjects, error: allSubProjectsError } = await supabase
        .from('sub_projects')
        .select('*')
        .neq('status', 'deleted');

      if (allSubProjectsError) {
        console.error('Alt projeler alınırken hata:', allSubProjectsError);
        toast.error('Alt projeler alınırken bir hata oluştu');
        return;
      }

      // console.log(`Found ${allSubProjects?.length || 0} total available sub-projects`);

      // Mevcut projenin alt projelerini çek
      const { data: currentSubProjects, error: currentSubProjectsError } = await supabase
        .from('project_sub_projects')
        .select('sub_project_id')
        .eq('project_id', project.id);

      if (currentSubProjectsError) {
        console.error('Alt projeler alınırken hata:', currentSubProjectsError);
        toast.error('Alt projeler alınırken bir hata oluştu');
        return;
      }

      // Seçili alt proje ID'lerini al
      const selectedIds = currentSubProjects?.map(sp => sp.sub_project_id) || [];
      // console.log(`Found ${selectedIds.length} currently linked sub-projects for this project:`, selectedIds);

      // Tüm alt projeleri state'e kaydet
      setSubProjects(prev => ({
        ...prev,
        all: allSubProjects || [],
      }));

      // Mevcut projenin alt projelerinin ID'lerini seçili olarak ayarla
      setSelectedSubProjects(selectedIds);
      setIsSubProjectModalOpen(true);

    } catch (err) {
      console.error('Alt projeler alınırken hata:', err);
      toast.error('Alt projeler alınırken bir hata oluştu');
    } finally {
      setLoadingStates(prev => ({ ...prev, subProjects: false }));
    }
  };

  // Alt projeleri güncelle
  const handleUpdateSubProjects = async (projectId, selectedIds) => {
    try {
      setLoadingStates(prev => ({ ...prev, subProjects: true }));
      // console.log('Updating sub projects for project:', projectId);
      // console.log('Selected sub project IDs:', selectedIds);

      // Önce mevcut projede hangi alt projelerin olduğunu bulalım
      const { data: currentLinks, error: fetchError } = await supabase
        .from('project_sub_projects')
        .select('sub_project_id')
        .eq('project_id', projectId);

      if (fetchError) {
        console.error('Mevcut alt projeler alınırken hata:', fetchError);
        toast.error('Alt projeler güncellenirken bir hata oluştu');
        return;
      }

      // Mevcut bağlantıların ID'lerini alalım
      const currentSubProjectIds = currentLinks.map(link => link.sub_project_id);
      // console.log('Mevcut alt proje ID\'leri:', currentSubProjectIds);
      
      // Kaldırılan alt projeleri belirle (önceden varken şimdi seçili olmayanlar)
      const removedSubProjectIds = currentSubProjectIds.filter(id => !selectedIds.includes(id));
      // console.log('Kaldırılan alt proje ID\'leri:', removedSubProjectIds);

      // Önce mevcut projenin tüm alt proje ilişkilerini sil
      const { error: deleteError } = await supabase
        .from('project_sub_projects')
        .delete()
        .eq('project_id', projectId);

      if (deleteError) {
        console.error('Alt projeler silinirken hata:', deleteError);
        toast.error('Alt projeler güncellenirken bir hata oluştu');
        return;
      }

      // Kaldırılan alt projelerin parent_project_id değerini null yap
      if (removedSubProjectIds.length > 0) {
        const { error: updateError } = await supabase
          .from('sub_projects')
          .update({ parent_project_id: null })
          .in('id', removedSubProjectIds);

        if (updateError) {
          console.error('Alt projelerin parent_project_id değeri güncellenirken hata:', updateError);
          toast.error('Alt projeler güncellenirken bir hata oluştu');
          return;
        }
        // console.log(`${removedSubProjectIds.length} alt projenin parent_project_id değeri null olarak güncellendi`);
      }

      // Seçili alt projeleri ekle
      if (selectedIds.length > 0) {
        const subProjectData = selectedIds.map(subProjectId => ({
          project_id: projectId,
          sub_project_id: subProjectId
        }));

        const { error: insertError } = await supabase
          .from('project_sub_projects')
          .insert(subProjectData);

        if (insertError) {
          console.error('Alt projeler eklenirken hata:', insertError);
          toast.error('Alt projeler güncellenirken bir hata oluştu');
          return;
        }
        
        // Eklenen alt projelerin parent_project_id değerini güncelle
        const { error: updateParentError } = await supabase
          .from('sub_projects')
          .update({ parent_project_id: projectId })
          .in('id', selectedIds);

        if (updateParentError) {
          console.error('Alt projelerin parent_project_id değeri güncellenirken hata:', updateParentError);
          toast.error('Alt projeler güncellenirken bir hata oluştu');
          return;
        }
        
        toast.success(`${selectedIds.length} alt proje başarıyla eklendi`);
      } else {
        // Hiç alt proje seçilmemiş, tümü kaldırıldı
        // console.log('Tüm alt projeler kaldırıldı');
        toast.success('Tüm alt projeler kaldırıldı');
      }

      // Alt projeleri yeniden yükle
      await fetchSubProjects(projectId);

    } catch (err) {
      console.error('Alt projeler güncellenirken hata:', err);
      toast.error('Alt projeler güncellenirken bir hata oluştu');
    } finally {
      setLoadingStates(prev => ({ ...prev, subProjects: false }));
    }
  };

  // Yeni proje oluştur
  const handleCreateProject = async () => {
    if (!newProject.name.trim()) {
      toast.error('Proje adı boş olamaz');
      return;
    }

    if (!newProject.start_date) {
      toast.error('Başlangıç tarihi seçilmeli');
      return;
    }

    try {
      setLoadingStates(prev => ({ ...prev, creation: true }));

      // Kullanıcı bilgisini al
      const { data: { user } } = await supabase.auth.getUser();

      const projectData = {
        name: newProject.name,
        description: newProject.description,
        start_date: newProject.start_date,
        end_date: newProject.end_date,
        status: 'active',
        created_by: user.id,
        task_count: 0,
        team_size: 0,
        completed_tasks: 0
      };

      // Projeyi oluştur
      const { data: project, error: projectError } = await supabase
        .from('projects')
        .insert([projectData])
        .select()
        .single();

      if (projectError) throw projectError;

      // Seçili kategorileri ekle
      if (newProject.categories.length > 0) {
        const categoryData = newProject.categories.map(categoryId => ({
          project_id: project.id,
          category_id: categoryId
        }));

        const { error: categoryError } = await supabase
          .from('project_categories')
          .insert(categoryData);

        if (categoryError) throw categoryError;
      }

      // Seçili alt projeleri ekle
      if (newProject.sub_projects.length > 0) {
        const subProjectData = newProject.sub_projects.map(subProjectId => ({
          project_id: project.id,
          sub_project_id: subProjectId
        }));

        const { error: subProjectError } = await supabase
          .from('project_sub_projects')
          .insert(subProjectData);

        if (subProjectError) throw subProjectError;
      }

      // Kullanıcı profil bilgilerini al
      const { data: userProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('first_name, last_name')
        .eq('id', user.id)
        .single();

      if (!profileError && userProfile) {
        const creatorName = `${userProfile.first_name} ${userProfile.last_name}`;
        
        // Bildirimleri oluştur
        try {
          await notifyProjectCreation(project, user.id, creatorName);
          // console.log('Proje bildirimleri başarıyla gönderildi');
        } catch (notificationError) {
          console.error('Bildirim gönderme hatası:', notificationError);
          // Bildirim hatası proje oluşturmayı engellemez
        }
      }

      // Projeleri yeniden yükle
      await fetchProjects();
      
      setIsCreateModalOpen(false);
      setSelectedProjectType(null);
      setNewProject({
        name: '',
        description: '',
        start_date: '',
        end_date: '',
        status: 'active',
        task_count: 0,
        team_size: 0,
        completed_tasks: 0,
        categories: [],
        sub_projects: [],
        has_end_date: true
      });
      toast.success('Proje başarıyla oluşturuldu');
    } catch (err) {
      console.error('Proje oluşturulurken hata:', err);
      toast.error('Proje oluşturulurken bir hata oluştu');
    } finally {
      setLoadingStates(prev => ({ ...prev, creation: false }));
    }
  };

  // Alt proje oluştur
  const handleCreateSubProject = async () => {
    if (!newSubProject.name.trim()) {
      toast.error('Alt proje adı boş olamaz');
      return;
    }

    if (!newSubProject.parent_project_id) {
      toast.error('Lütfen bir proje seçin');
      return;
    }

    // Basit tarih validasyonu
    if (newSubProject.has_end_date && newSubProject.end_date && newSubProject.start_date) {
      const startDate = new Date(newSubProject.start_date);
      const endDate = new Date(newSubProject.end_date);
      
      // Bitiş tarihi başlangıç tarihinden önce olamaz
      if (endDate < startDate) {
        toast.error('Bitiş tarihi, başlangıç tarihinden önce olamaz');
        return;
      }
    }

    // Eğer bitiş tarihi belirle seçiliyse ama tarih girilmemişse uyar
    if (newSubProject.has_end_date && !newSubProject.end_date) {
      toast.error('Bitiş tarihi belirle seçeneği aktifken bitiş tarihi seçmelisiniz');
      return;
    }

    try {
      setLoadingStates(prev => ({ ...prev, creation: true }));

      // Kullanıcı bilgisini al
      const { data: { user } } = await supabase.auth.getUser();

      const subProjectData = {
        name: newSubProject.name,
        description: newSubProject.description || '',
        parent_project_id: newSubProject.parent_project_id,
        status: 'active',
        team_size: 0,
        task_count: 0,
        completed_tasks: 0,
        start_date: newSubProject.start_date || new Date().toISOString().split('T')[0],
        end_date: newSubProject.has_end_date && newSubProject.end_date ? newSubProject.end_date : null
      };

      const { data: subProject, error: subProjectError } = await supabase
        .from('sub_projects')
        .insert([subProjectData])
        .select()
        .single();

      if (subProjectError) throw subProjectError;

      // Alt projeyi proje ile ilişkilendir (project_sub_projects tablosuna ekleme)
      const { error: relationError } = await supabase
        .from('project_sub_projects')
        .insert({
          project_id: newSubProject.parent_project_id,
          sub_project_id: subProject.id
        });

      if (relationError) {
        console.error('Alt proje ilişkilendirme hatası:', relationError);
        toast.error('Alt proje oluşturuldu ancak projeye bağlanırken bir hata oluştu');
        return;
      }

      // Ana projenin adını al
      const { data: parentProject, error: parentError } = await supabase
        .from('projects')
        .select('name')
        .eq('id', newSubProject.parent_project_id)
        .single();

      if (!parentError && parentProject) {
        try {
          await notifySubProjectCreation(
            subProject, 
            newSubProject.parent_project_id, 
            parentProject.name, 
            user.id
          );
          // console.log('Alt proje bildirimleri başarıyla gönderildi');
        } catch (notificationError) {
          console.error('Alt proje bildirim hatası:', notificationError);
          // Bildirim hatası alt proje oluşturmayı engellemez
        }
      }

      // Alt projeleri yeniden yükle
      await fetchSubProjects(newSubProject.parent_project_id);
      
      setIsCreateModalOpen(false);
      setSelectedProjectType(null);
      setNewSubProject({
        name: '',
        description: '',
        parent_project_id: null,
        start_date: '',
        end_date: '',
        has_end_date: false
      });
      toast.success('Alt proje başarıyla oluşturuldu');
    } catch (err) {
      console.error('Alt proje oluşturulurken hata:', err);
      toast.error('Alt proje oluşturulurken bir hata oluştu');
    } finally {
      setLoadingStates(prev => ({ ...prev, creation: false }));
    }
  };

  const [subProjectSearch, setSubProjectSearch] = useState('');
  const [subProjectSort, setSubProjectSort] = useState('name-asc'); // 'name-asc', 'name-desc', 'status', 'team-size'

  // Kategorileri çek
  const fetchCategories = async () => {
    try {
      setLoadingStates(prev => ({ ...prev, categories: true }));
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('name');

      if (error) {
        console.error('Kategoriler alınırken hata:', error);
        toast.error('Kategoriler alınırken bir hata oluştu');
        return;
      }

      setCategories(data || []);
    } catch (err) {
      console.error('Kategoriler alınırken hata:', err);
      toast.error('Kategoriler alınırken bir hata oluştu');
    } finally {
      setLoadingStates(prev => ({ ...prev, categories: false }));
    }
  };

  // Yeni kategori oluştur
  const handleCreateCategory = async () => {
    if (!newCategory.name.trim()) {
      toast.error('Kategori adı boş olamaz');
      return;
    }

    try {
      setLoadingStates(prev => ({ ...prev, creation: true }));
      const { data, error } = await supabase
        .from('categories')
        .insert([{
          name: newCategory.name,
          color: newCategory.color
        }])
        .select()
        .single();

      if (error) throw error;

      setCategories(prev => [...prev, data]);
      setNewCategory({ name: '', color: 'blue' });
      setIsNewCategoryModalOpen(false);
      toast.success('Kategori başarıyla oluşturuldu');
    } catch (err) {
      console.error('Kategori oluşturulurken hata:', err);
      toast.error('Kategori oluşturulurken bir hata oluştu');
    } finally {
      setLoadingStates(prev => ({ ...prev, creation: false }));
    }
  };

  // Kategori güncelle
  const handleUpdateCategory = async (categoryId) => {
    if (!editingCategory.name.trim()) {
      toast.error('Kategori adı boş olamaz');
      return;
    }

    try {
      setLoadingStates(prev => ({ ...prev, categories: true }));
      const { error } = await supabase
        .from('categories')
        .update({
          name: editingCategory.name,
          color: editingCategory.color
        })
        .eq('id', categoryId);

      if (error) throw error;

      setCategories(prev => prev.map(cat => 
        cat.id === categoryId ? { ...cat, ...editingCategory } : cat
      ));
      setEditingCategory(null);
      toast.success('Kategori başarıyla güncellendi');
    } catch (err) {
      console.error('Kategori güncellenirken hata:', err);
      toast.error('Kategori güncellenirken bir hata oluştu');
    } finally {
      setLoadingStates(prev => ({ ...prev, categories: false }));
    }
  };

  // Kategori sil
  const handleDeleteCategory = async (categoryId) => {
    try {
      setLoadingStates(prev => ({ ...prev, categories: true }));
      const { error } = await supabase
        .from('categories')
        .delete()
        .eq('id', categoryId);

      if (error) throw error;

      setCategories(prev => prev.filter(cat => cat.id !== categoryId));
      toast.success('Kategori başarıyla silindi');
    } catch (err) {
      console.error('Kategori silinirken hata:', err);
      toast.error('Kategori silinirken bir hata oluştu');
    } finally {
      setLoadingStates(prev => ({ ...prev, categories: false }));
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [searchQuery, statusFilter]);

  useEffect(() => {
    if (selectedProjectType === 'categories') {
      fetchCategories();
    }
  }, [selectedProjectType]);

  // Modal açıldığında body scroll'u engelle
  useEffect(() => {
    if (isProjectModalOpen || isCreateModalOpen || isSubProjectModalOpen || isNewCategoryModalOpen || isEditModalOpen || isDeleteModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    // Component unmount olduğunda overflow'u tekrar auto yap
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isProjectModalOpen, isCreateModalOpen, isSubProjectModalOpen, isNewCategoryModalOpen, isEditModalOpen, isDeleteModalOpen]);

  // Yeni proje modalı açıldığında kategorileri ve alt projeleri yükle, kapandığında tüm projeleri tekrar getir
  useEffect(() => {
    if (isCreateModalOpen && selectedProjectType === 'project') {
      // Kategorileri yükle
      fetchCategories();
      
      // Tüm aktif alt projeleri yükle
      const fetchAvailableSubProjects = async () => {
        try {
          setLoadingStates(prev => ({ ...prev, subProjects: true }));
          const { data, error } = await supabase
            .from('sub_projects')
            .select('*')
            .eq('status', 'active');

          if (error) throw error;

          setSubProjects(prev => ({
            ...prev,
            all: data || []
          }));
        } catch (err) {
          console.error('Alt projeler yüklenirken hata:', err);
          toast.error('Alt projeler yüklenirken bir hata oluştu');
        } finally {
          setLoadingStates(prev => ({ ...prev, subProjects: false }));
        }
      };

      fetchAvailableSubProjects();
    } else if (isCreateModalOpen && selectedProjectType === 'subproject') {
      // Alt proje oluşturma ekranı için sadece adı "Projeler" olan projeleri getir
      const fetchProjectsForSubProject = async () => {
        try {
          setLoadingStates(prev => ({ ...prev, projects: true }));
          const { data, error } = await supabase
            .from('projects')
            .select('*')
            .eq('name', 'Projeler');

          if (error) {
            console.error('Projeler alınırken hata oluştu:', error);
            toast.error('Projeler alınırken bir hata oluştu');
            return;
          }

          setProjects(data || []);
          
          // İlk projeyi otomatik seç
          if (data && data.length > 0) {
            setNewSubProject(prev => ({
              ...prev,
              parent_project_id: data[0].id
            }));
          }
        } catch (err) {
          console.error('Projeler alınırken hata oluştu:', err);
          toast.error('Projeler alınırken bir hata oluştu');
        } finally {
          setLoadingStates(prev => ({ ...prev, projects: false }));
        }
      };

      fetchProjectsForSubProject();
    } else if (!isCreateModalOpen || !selectedProjectType) {
      // Eğer modal kapandıysa veya proje tipi seçilmediyse tüm projeleri tekrar getir
      fetchProjects();
      
      // Modal kapandığında form state'ini sıfırla
      if (!isCreateModalOpen) {
        setNewSubProject({
          name: '',
          description: '',
          parent_project_id: null,
          start_date: '',
          end_date: '',
          has_end_date: false
        });
      }
    }
  }, [isCreateModalOpen, selectedProjectType]);

  const [userRole, setUserRole] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [showOnlyMyProjects, setShowOnlyMyProjects] = useState(false);

  // Fetch current user and role
  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: userProfile } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', user.id)
          .single();
        setCurrentUser(userProfile);
        setUserRole(userProfile?.role);
        if (userProfile?.role !== 'super') {
          setShowOnlyMyProjects(true);
        }
      }
    };
    getCurrentUser();
  }, []);

  // Permission-based project filtering
  const permissionFilteredProjects = useMemo(() => {
    return projects.filter(subProject => {
      if (!currentUser || !userRole) return false;
      if (userRole === 'super' && !showOnlyMyProjects) return true;
      if (userRole === 'super' && showOnlyMyProjects) {
        // Only show sub-projects where user is assigned (if assignment exists)
        return subProject.assigned_members && subProject.assigned_members.some(m => m.id === currentUser.id);
      }
      if (userRole === 'admin') {
        // Admins see sub-projects they created or are assigned to
        return (subProject.created_by === currentUser.id) || (subProject.assigned_members && subProject.assigned_members.some(m => m.id === currentUser.id));
      }
      // Regular users see only assigned sub-projects
      return subProject.assigned_members && subProject.assigned_members.some(m => m.id === currentUser.id);
    });
  }, [projects, currentUser, userRole, showOnlyMyProjects]);

  // Proje düzenle
  const handleEditProject = async () => {
    if (!editingProject.name.trim()) {
      toast.error('Proje adı boş olamaz');
      return;
    }

    if (!editingProject.start_date) {
      toast.error('Başlangıç tarihi seçilmeli');
      return;
    }

    // Tarih validasyonu
    if (editingProject.has_end_date && editingProject.end_date && editingProject.start_date) {
      const startDate = new Date(editingProject.start_date);
      const endDate = new Date(editingProject.end_date);
      
      if (endDate < startDate) {
        toast.error('Bitiş tarihi, başlangıç tarihinden önce olamaz');
        return;
      }
    }

    // Eğer bitiş tarihi belirle seçiliyse ama tarih girilmemişse uyar
    if (editingProject.has_end_date && !editingProject.end_date) {
      toast.error('Bitiş tarihi belirle seçeneği aktifken bitiş tarihi seçmelisiniz');
      return;
    }

    try {
      setLoadingStates(prev => ({ ...prev, creation: true }));

      const projectData = {
        name: editingProject.name,
        description: editingProject.description,
        start_date: editingProject.start_date,
        end_date: editingProject.has_end_date && editingProject.end_date ? editingProject.end_date : null,
        status: editingProject.status
      };

      // Projeyi güncelle
      const { error: projectError } = await supabase
        .from('sub_projects')
        .update(projectData)
        .eq('id', editingProject.id);

      if (projectError) throw projectError;

      // Projeleri yeniden yükle
      await fetchProjects();
      
      setIsEditModalOpen(false);
      setEditingProject(null);
      toast.success('Proje başarıyla güncellendi');
    } catch (err) {
      console.error('Proje güncellenirken hata:', err);
      toast.error('Proje güncellenirken bir hata oluştu');
    } finally {
      setLoadingStates(prev => ({ ...prev, creation: false }));
    }
  };

  // Proje sil
  const handleDeleteProject = async () => {
    if (!projectToDelete) return;

    try {
      setLoadingStates(prev => ({ ...prev, creation: true }));

      // Önce project_sub_projects tablosundan bu projeyle ilgili kayıtları sil
      const { error: projectSubProjectsError } = await supabase
        .from('project_sub_projects')
        .delete()
        .eq('sub_project_id', projectToDelete.id);

      if (projectSubProjectsError) {
        console.error('Proje ilişkileri silinirken hata:', projectSubProjectsError);
        toast.error('Proje ilişkileri silinirken bir hata oluştu');
        return;
      }

      // Sonra projeyi gerçekten sil (hard delete)
      const { error } = await supabase
        .from('sub_projects')
        .delete()
        .eq('id', projectToDelete.id);

      if (error) throw error;

      // Projeleri yeniden yükle
      await fetchProjects();
      
      setIsDeleteModalOpen(false);
      setProjectToDelete(null);
      toast.success('Proje başarıyla silindi');
    } catch (err) {
      console.error('Proje silinirken hata:', err);
      toast.error('Proje silinirken bir hata oluştu');
    } finally {
      setLoadingStates(prev => ({ ...prev, creation: false }));
    }
  };

  // Düzenleme modalını aç
  const openEditModal = (project) => {
    setEditingProject({
      ...project,
      has_end_date: !!project.end_date
    });
    setIsEditModalOpen(true);
  };

  // Silme modalını aç
  const openDeleteModal = (project) => {
    setProjectToDelete(project);
    setIsDeleteModalOpen(true);
  };

  // Görevleri görüntüle - Proje ID'si ile görevler sayfasına yönlendir
  const handleViewTasks = (projectId) => {
    // Tasks sayfasına proje filtresiyle yönlendir
    navigate(`/tasks?project=${projectId}`);
    setIsProjectModalOpen(false);
    toast.success('Görevler sayfasına yönlendiriliyor...');
  };

  // Proje raporu oluştur
  const handleGenerateReport = async (project) => {
    try {
      setLoadingStates(prev => ({ ...prev, creation: true }));
      
      // Proje verilerini topla
      const [tasksResult, categoriesResult] = await Promise.all([
        // Bu projeye ait görevleri getir
        supabase
          .from('tasks')
          .select('id, title, status, priority, created_at, end_date, assigned_members')
          .eq('sub_project_id', project.id),
        
        // Proje kategorilerini getir
        supabase
          .from('project_categories')
          .select(`
            *,
            category:categories(*)
          `)
          .eq('project_id', project.id)
      ]);

      if (tasksResult.error) throw tasksResult.error;
      if (categoriesResult.error) throw categoriesResult.error;

      const tasks = tasksResult.data || [];
      const categories = categoriesResult.data || [];

      // Rapor verilerini hazırla
      const reportData = {
        project: {
          id: project.id,
          name: project.name,
          description: project.description,
          status: project.status,
          start_date: project.start_date,
          end_date: project.end_date,
          task_count: project.task_count || 0,
          completed_tasks: project.completed_tasks || 0,
          team_size: project.team_size || 0
        },
        tasks: tasks,
        categories: categories.map(pc => pc.category),
        statistics: {
          total_tasks: tasks.length,
          completed_tasks: tasks.filter(t => t.status === 'done').length,
          in_progress_tasks: tasks.filter(t => t.status === 'in_progress').length,
          pending_tasks: tasks.filter(t => t.status === 'todo').length,
          high_priority_tasks: tasks.filter(t => t.priority === 'high').length,
          overdue_tasks: tasks.filter(t => t.end_date && new Date(t.end_date) < new Date() && t.status !== 'done').length
        },
        generated_at: new Date().toISOString(),
        generated_by: currentUser?.id
      };

      // Raporu JSON formatında indir
      const dataStr = JSON.stringify(reportData, null, 2);
      const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
      
      const exportFileDefaultName = `${project.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_raporu_${new Date().toISOString().split('T')[0]}.json`;
      
      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', exportFileDefaultName);
      linkElement.click();

      toast.success('Proje raporu başarıyla oluşturuldu ve indirildi');
      
    } catch (error) {
      console.error('Rapor oluşturma hatası:', error);
      toast.error('Rapor oluşturulurken bir hata oluştu');
    } finally {
      setLoadingStates(prev => ({ ...prev, creation: false }));
    }
  };

  // Proje ayarları
  const handleProjectSettings = (project) => {
    toast.info('Proje ayarları özelliği yakında eklenecek');
  };

  // Proje istatistikleri detayları
  const handleViewStatistics = (project) => {
    // Modal içinde istatistik detaylarını göster
    const statsModal = {
      project: project,
      showDetails: true
    };
    
    // Bu fonksiyon gelecekte ayrı bir modal açabilir
    toast.info('Detaylı istatistikler özelliği yakında eklenecek');
  };

  // Proje raporu oluştur - Quick Actions için wrapper fonksiyon
  const generateProjectReport = (project) => {
    handleGenerateReport(project);
  };

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      <div className="flex justify-between items-center mt-2 mb-8">
        {/* Permission level indicator */}
        {userRole && (
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
            userRole === 'super' 
              ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' 
              : userRole === 'admin' 
                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' 
                : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
          }`}>
            {userRole === 'super' ? (
              <Shield className="w-4 h-4" />
            ) : userRole === 'admin' ? (
              <User className="w-4 h-4" />
            ) : (
              <Users2 className="w-4 h-4" />
            )}
            <span className="text-sm font-medium">
              {userRole === 'super' 
                ? 'Süper Yönetici' 
                : userRole === 'admin' 
                  ? 'Yönetici' 
                  : 'Standart Kullanıcı'
              }
            </span>
          </div>
        )}
        {/* Super user filter toggle */}
        {userRole === 'super' && (
          <div className="relative mr-2">
            <button
              type="button"
              onClick={() => setShowOnlyMyProjects(!showOnlyMyProjects)}
              className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                showOnlyMyProjects
                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                  : 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400'
              }`}
            >
              {showOnlyMyProjects ? (
                <Users2 className="w-4 h-4" />
              ) : (
                <Folder className="w-4 h-4" />
              )}
              {showOnlyMyProjects ? 'Projelerim' : 'Tüm Projeler'}
            </button>
          </div>
        )}
      </div>
      {/* Projeler Listesi */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-8">
        <div className="mb-6 space-y-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
              Projeler
            </h3>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Toplam: {projects.length} Proje Mevcut
            </div>
          </div>

          {/* Arama ve Filtreleme */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input 
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Proje ara..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="flex items-center gap-2 min-w-[200px]">
              <Filter className="w-5 h-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
              >
                <option value="all">Tüm Durumlar</option>
                <option value="active">Aktif</option>
                <option value="completed">Tamamlandı</option>
                <option value="pending">Beklemede</option>
              </select>
            </div>
          </div>
        </div>

        {loadingStates.projects ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          </div>
        ) : permissionFilteredProjects.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {permissionFilteredProjects.map((subProject) => (
              <motion.div
                key={subProject.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-primary-500 dark:hover:border-primary-500 cursor-pointer transition-colors relative group"
              >
                {/* Düzenle ve Sil Butonları - Sadece super user için */}
                {userRole === 'super' && (
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity mt-12">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditModal(subProject);
                      }}
                      className="p-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-md transition-colors"
                      title="Projeyi Düzenle"
                    >
                      <Edit2 className="w-3 h-3" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openDeleteModal(subProject);
                      }}
                      className="p-1.5 bg-red-500 hover:bg-red-600 text-white rounded-md transition-colors"
                      title="Projeyi Sil"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}

                <div 
                  onClick={() => handleProjectClick(subProject)}
                  className="cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-3">
                    <h4 className="text-base font-medium text-gray-900 dark:text-white pr-16">
                      {subProject.name}
                    </h4>
                    <div
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        subProject.status === 'active'
                          ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                          : subProject.status === 'completed'
                          ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
                          : 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400'
                      }`}
                    >
                      {subProject.status === 'active'
                        ? 'Aktif'
                        : subProject.status === 'completed'
                        ? 'Tamamlandı'
                        : 'Beklemede'}
                    </div>
                  </div>

                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 line-clamp-2 ">
                    {subProject.description || 'Açıklama bulunmuyor'}
                  </p>

                  <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                    <div className="flex items-center gap-2">
                      <Users2 className="w-4 h-4" />
                      <span>{subProject.team_size || 0} Üye</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <BarChart2 className="w-4 h-4" />
                      <span>{subProject.task_count || 0} Görev</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="mb-4">
              <Folder className="w-12 h-12 mx-auto text-gray-400 animate-pulse" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2 animate-pulse">
              Proje Bulunamadı
            </h3>
            <p className="text-gray-500 dark:text-gray-400 animate-pulse">
              {searchQuery || statusFilter !== 'all'
                ? 'Arama kriterlerinize uygun proje bulunamadı. Filtreleri değiştirmeyi deneyin.'
                : 'Henüz hiç proje oluşturulmamış. Yeni bir proje oluşturmak için "Yeni Oluştur" butonuna tıklayın.'}
            </p>
          </div>
        )}
      </div>

      {/* Yeni Proje Butonu */}
      <div className="flex justify-center mt-6">
        {userRole === 'super' ? (
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="w-full max-w-4xl flex items-center justify-center gap-2 px-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary-500 dark:hover:border-primary-500 transition-colors"
          >
            <Plus className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            <span className="text-gray-600 dark:text-gray-300">Yeni Oluştur</span>
          </button>
        ) : (
          <button
            disabled
            className="w-full max-w-4xl flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-400 cursor-not-allowed opacity-60"
            title="Sadece Süper Yönetici yeni proje oluşturabilir"
          >
            <Plus className="w-5 h-5 text-gray-400" />
            <span className="text-gray-400">Yeni Oluştur</span>
          </button>
        )}
      </div>

      {/* Proje Detay Modal */}
      <AnimatePresence>
        {isProjectModalOpen && selectedProjectForModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Modal arkaplanı */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => {
                setIsProjectModalOpen(false);
                setSelectedProjectForModal(null);
                setSelectedSubProjects([]);
                setShowAllSubProjects(false);
              }}
            />
            {/* Modal içeriği */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="relative w-[95vw] max-w-6xl bg-white dark:bg-gray-800 rounded-xl shadow-xl overflow-hidden"
              style={{ maxHeight: '90vh' }}
            >
              <div className="flex flex-col" style={{ height: '90vh' }}>
                {/* Header */}
                <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-primary-50 to-blue-50 dark:from-gray-800 dark:to-gray-700">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`p-2 rounded-lg ${
                          selectedProjectForModal.status === 'active'
                            ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400'
                            : selectedProjectForModal.status === 'completed'
                            ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                            : 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400'
                        }`}>
                          <Folder className="w-5 h-5" />
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                            {selectedProjectForModal.name}
                          </h3>
                          <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400 mt-1">
                            <div className="flex items-center gap-1">
                              <CalendarIcon className="w-4 h-4" />
                              <span>
                                Başlangıç: {selectedProjectForModal.start_date
                                  ? new Date(selectedProjectForModal.start_date).toLocaleDateString('tr-TR')
                                  : 'Belirtilmemiş'}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Clock3 className="w-4 h-4" />
                              <span>
                                Bitiş: {selectedProjectForModal.end_date
                                  ? new Date(selectedProjectForModal.end_date).toLocaleDateString('tr-TR')
                                  : 'Belirsiz'}
                              </span>
                            </div>
                            <div className={`px-3 py-1 text-xs font-medium rounded-full ${
                              selectedProjectForModal.status === 'active'
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                : selectedProjectForModal.status === 'completed'
                                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                            }`}>
                              {selectedProjectForModal.status === 'active'
                                ? 'Aktif'
                                : selectedProjectForModal.status === 'completed'
                                ? 'Tamamlandı'
                                : 'Beklemede'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {userRole === 'super' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditModal(selectedProjectForModal);
                            setIsProjectModalOpen(false);
                          }}
                          className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
                          title="Projeyi Düzenle"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setIsProjectModalOpen(false);
                          setSelectedProjectForModal(null);
                          setSelectedSubProjects([]);
                          setShowAllSubProjects(false);
                        }}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      >
                        <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Content - Scrollable Area */}
                <div className="flex-1 overflow-y-auto p-6" style={{ minHeight: 0 }}>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Sol Sütun - Ana Bilgiler */}
                    <div className="lg:col-span-2 space-y-6">
                      {/* Proje Açıklaması */}
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                          <Tag className="w-5 h-5 text-primary-500" />
                          Proje Açıklaması
                        </h4>
                        <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                          {selectedProjectForModal.description || 'Bu proje için henüz bir açıklama eklenmemiş. Proje hakkında detaylı bilgi eklemek için düzenleme yapabilirsiniz.'}
                        </p>
                      </div>

                      {/* İlerleme Durumu */}
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                          <BarChart2 className="w-5 h-5 text-primary-500" />
                          Proje İlerlemesi
                        </h4>
                        
                        {/* İlerleme Çubuğu */}
                        <div className="mb-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              Tamamlanma Oranı
                            </span>
                            <span className="text-sm font-bold text-primary-600 dark:text-primary-400">
                              {selectedProjectForModal.task_count > 0 
                                ? Math.round((selectedProjectForModal.completed_tasks / selectedProjectForModal.task_count) * 100)
                                : 0}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-3">
                            <div
                              className="bg-gradient-to-r from-primary-500 to-blue-500 h-3 rounded-full transition-all duration-300"
                              style={{
                                width: selectedProjectForModal.task_count > 0 
                                  ? `${Math.round((selectedProjectForModal.completed_tasks / selectedProjectForModal.task_count) * 100)}%`
                                  : '0%'
                              }}
                            />
                          </div>
                        </div>

                        {/* Görev Detayları */}
                        <div className="grid grid-cols-3 gap-4">
                          <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                            <div className="text-2xl font-bold text-gray-900 dark:text-white">
                              {selectedProjectForModal.task_count || 0}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              Toplam Görev
                            </div>
                          </div>
                          <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                              {selectedProjectForModal.completed_tasks || 0}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              Tamamlanan
                            </div>
                          </div>
                          <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                            <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                              {(selectedProjectForModal.task_count || 0) - (selectedProjectForModal.completed_tasks || 0)}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              Devam Eden
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Zaman Çizelgesi */}
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                          <CalendarIcon className="w-5 h-5 text-primary-500" />
                          Proje Zaman Çizelgesi
                        </h4>
                        <div className="space-y-3">
                          <div className="flex items-center gap-3 p-3 bg-white dark:bg-gray-800 rounded-lg">
                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                            <div className="flex-1">
                              <div className="font-medium text-gray-900 dark:text-white">Proje Başlangıcı</div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                {selectedProjectForModal.start_date
                                  ? new Date(selectedProjectForModal.start_date).toLocaleDateString('tr-TR', {
                                      weekday: 'long',
                                      year: 'numeric',
                                      month: 'long',
                                      day: 'numeric'
                                    })
                                  : 'Tarih belirlenmemiş'}
                              </div>
                            </div>
                          </div>
                          
                          {selectedProjectForModal.end_date && (
                            <div className="flex items-center gap-3 p-3 bg-white dark:bg-gray-800 rounded-lg">
                              <div className={`w-3 h-3 rounded-full ${
                                new Date(selectedProjectForModal.end_date) < new Date() 
                                  ? 'bg-red-500' 
                                  : 'bg-blue-500'
                              }`}></div>
                              <div className="flex-1">
                                <div className="font-medium text-gray-900 dark:text-white">
                                  {new Date(selectedProjectForModal.end_date) < new Date() 
                                    ? 'Proje Süresi Doldu' 
                                    : 'Planlanan Bitiş'}
                                </div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  {new Date(selectedProjectForModal.end_date).toLocaleDateString('tr-TR', {
                                    weekday: 'long',
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                  })}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Kalan Süre */}
                          {selectedProjectForModal.end_date && (
                            <div className="mt-4 p-3 bg-primary-50 dark:bg-primary-900/30 rounded-lg">
                              <div className="text-sm font-medium text-primary-700 dark:text-primary-300">
                                {(() => {
                                  const now = new Date();
                                  const endDate = new Date(selectedProjectForModal.end_date);
                                  const diffTime = endDate - now;
                                  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                                  
                                  if (diffDays < 0) {
                                    return `Proje süresi ${Math.abs(diffDays)} gün önce doldu`;
                                  } else if (diffDays === 0) {
                                    return 'Proje bugün bitiyor';
                                  } else if (diffDays === 1) {
                                    return 'Proje yarın bitiyor';
                                  } else {
                                    return `Projenin bitmesine ${diffDays} gün kaldı`;
                                  }
                                })()}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Sağ Sütun - İstatistikler ve Eylemler */}
                    <div className="space-y-6">
                      {/* Hızlı İstatistikler */}
                      <div className="bg-gradient-to-br from-primary-50 to-blue-50 dark:from-gray-700 dark:to-gray-600 rounded-xl p-6">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                          <BarChart2 className="w-5 h-5 text-primary-500" />
                          Proje İstatistikleri
                        </h4>
                        <div className="space-y-4">
                          <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                                <BarChart2 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                              </div>
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Toplam Görev
                              </span>
                            </div>
                            <span className="text-xl font-bold text-gray-900 dark:text-white">
                              {selectedProjectForModal.task_count || 0}
                            </span>
                          </div>

                          <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                                <Users2 className="w-4 h-4 text-green-600 dark:text-green-400" />
                              </div>
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Ekip Üyeleri
                              </span>
                            </div>
                            <span className="text-xl font-bold text-gray-900 dark:text-white">
                              {selectedProjectForModal.team_size || 0}
                            </span>
                          </div>

                          <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                                <CheckCircle2 className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                              </div>
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Tamamlanan
                              </span>
                            </div>
                            <span className="text-xl font-bold text-gray-900 dark:text-white">
                              {selectedProjectForModal.completed_tasks || 0}
                            </span>
                          </div>

                          <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                                <Clock3 className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                              </div>
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Başarı Oranı
                              </span>
                            </div>
                            <span className="text-xl font-bold text-gray-900 dark:text-white">
                              {selectedProjectForModal.task_count > 0 
                                ? Math.round((selectedProjectForModal.completed_tasks / selectedProjectForModal.task_count) * 100)
                                : 0}%
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Proje Durumu Kartı */}
                      <div className="bg-white dark:bg-gray-700 rounded-xl p-6 border border-gray-200 dark:border-gray-600">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                          Proje Durumu
                        </h4>
                        <div className="space-y-3">
                          <div className={`p-3 rounded-lg ${
                            selectedProjectForModal.status === 'active'
                              ? 'bg-green-50 border border-green-200 dark:bg-green-900/20 dark:border-green-800'
                              : selectedProjectForModal.status === 'completed'
                              ? 'bg-blue-50 border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800'
                              : 'bg-yellow-50 border border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800'
                          }`}>
                            <div className="flex items-center gap-2">
                              <div className={`w-3 h-3 rounded-full ${
                                selectedProjectForModal.status === 'active'
                                  ? 'bg-green-500'
                                  : selectedProjectForModal.status === 'completed'
                                  ? 'bg-blue-500'
                                  : 'bg-yellow-500'
                              }`}></div>
                              <span className={`font-medium ${
                                selectedProjectForModal.status === 'active'
                                  ? 'text-green-700 dark:text-green-400'
                                  : selectedProjectForModal.status === 'completed'
                                  ? 'text-blue-700 dark:text-blue-400'
                                  : 'text-yellow-700 dark:text-yellow-400'
                              }`}>
                                {selectedProjectForModal.status === 'active'
                                  ? 'Aktif Proje'
                                  : selectedProjectForModal.status === 'completed'
                                  ? 'Tamamlanan Proje'
                                  : 'Beklemede'}
                              </span>
                            </div>
                            <p className={`text-sm mt-1 ${
                              selectedProjectForModal.status === 'active'
                                ? 'text-green-600 dark:text-green-300'
                                : selectedProjectForModal.status === 'completed'
                                ? 'text-blue-600 dark:text-blue-300'
                                : 'text-yellow-600 dark:text-yellow-300'
                            }`}>
                              {selectedProjectForModal.status === 'active'
                                ? 'Proje aktif olarak devam ediyor'
                                : selectedProjectForModal.status === 'completed'
                                ? 'Proje başarıyla tamamlandı'
                                : 'Proje başlatılmayı bekliyor'}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Hızlı Eylemler */}
                      <div className="bg-white dark:bg-gray-700 rounded-xl p-6 border border-gray-200 dark:border-gray-600">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                          Hızlı Eylemler
                        </h4>
                        <div className="space-y-2">
                          <button 
                            onClick={() => handleViewTasks(selectedProjectForModal.id)}
                            className="w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50 dark:hover:bg-gray-600 rounded-lg transition-colors"
                          >
                            <BarChart2 className="w-5 h-5 text-primary-500" />
                            <span className="text-gray-700 dark:text-gray-300">Görevleri Görüntüle</span>
                          </button>
                          
                          {userRole === 'super' && (
                            <button
                              onClick={() => {
                                openEditModal(selectedProjectForModal);
                                setIsProjectModalOpen(false);
                              }}
                              className="w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50 dark:hover:bg-gray-600 rounded-lg transition-colors"
                            >
                              <Edit2 className="w-5 h-5 text-blue-500" />
                              <span className="text-gray-700 dark:text-gray-300">Projeyi Düzenle</span>
                            </button>
                          )}
                          
                          <button 
                            onClick={() => generateProjectReport(selectedProjectForModal)}
                            className="w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50 dark:hover:bg-gray-600 rounded-lg transition-colors"
                          >
                            <BarChart2 className="w-5 h-5 text-green-500" />
                            <span className="text-gray-700 dark:text-gray-300">Rapor Oluştur</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Proje/Alt Proje Oluşturma Modal */}
      <AnimatePresence>
        {isCreateModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => {
                setIsCreateModalOpen(false);
                setSelectedProjectType(null);
              }}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="relative w-[90vw] max-w-2xl max-h-[80vh] bg-white dark:bg-gray-800 rounded-xl shadow-xl overflow-y-auto"
            >
              <div className="p-6">
                {/* Üst Bar */}
                <div className="flex items-center justify-between mb-6">
                  <button
                    onClick={() => {
                      if (selectedProjectType) {
                        setSelectedProjectType(null);
                      } else {
                        setIsCreateModalOpen(false);
                      }
                    }}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                  >
                    <ArrowLeft className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  </button>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {!selectedProjectType ? 'Yeni Oluştur' : selectedProjectType === 'project' ? 'Proje Oluştur' : 'Yeni Proje Oluştur'}
                  </h3>
                  <button
                    onClick={() => {
                      setIsCreateModalOpen(false);
                      setSelectedProjectType(null);
                    }}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>

                {/* İçerik */}
                {!selectedProjectType ? (
                  // Proje Tipi Seçimi
                  <div className="space-y-4">
                    {/* Proje Ekleme */}
                    {/*}
                  <button
                      onClick={() => setSelectedProjectType('project')}
                      className="w-full p-4 text-left border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Folder className="w-6 h-6 text-primary-500" />
                        <div>
                          <h4 className="font-medium text-gray-900 dark:text-white">Proje</h4>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Yeni bir proje oluştur
                          </p>
                        </div>
                      </div>
                    </button>
                      */}
                    {/* Alt Proje Ekleme */}
                    <button
                      onClick={() => setSelectedProjectType('subproject')}
                      className="w-full p-4 text-left border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <FolderPlus className="w-6 h-6 text-primary-500" />
                        <div>
                          <h4 className="font-medium text-gray-900 dark:text-white">Proje Ekle</h4>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Bir proje ekleyin.
                          </p>
                        </div>
                      </div>
                    </button>
                      {/* Kategorileri Düzenle
                                          <button
                      onClick={() => setSelectedProjectType('categories')}
                      className="w-full p-4 text-left border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Settings className="w-6 h-6 text-primary-500" />
                        <div>
                          <h4 className="font-medium text-gray-900 dark:text-white">Kategorileri Düzenle</h4>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Proje kategorilerini yönet
                          </p>
                        </div>
                      </div>
                    </button>
                      
                      
                      */}
                  </div>
                ) : selectedProjectType === 'project' ? (
                  // Proje Oluşturma Formu
                  <div className="space-y-4">
                    {/* Proje Adı */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Proje Adı *
                      </label>
                      <input
                        type="text"
                        value={newProject.name}
                        onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                        placeholder="Proje adını girin"
                      />
                    </div>

                    {/* Proje Açıklaması */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Proje Açıklaması
                      </label>
                      <textarea
                        value={newProject.description}
                        onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                        rows="3"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                        placeholder="Proje açıklamasını girin"
                      />
                    </div>

                    {/* Tarihler */}
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Başlangıç Tarihi *
                        </label>
                        <input
                          type="date"
                          value={newProject.start_date}
                          onChange={(e) => {
                            const newStartDate = e.target.value;
                            setNewProject(prev => ({
                              ...prev,
                              start_date: newStartDate,
                              end_date: prev.end_date && newStartDate > prev.end_date ? '' : prev.end_date
                            }));
                          }}
                          max={newProject.end_date || undefined}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="hasEndDate"
                            checked={newProject.has_end_date}
                            onChange={(e) => {
                              setNewProject(prev => ({
                                ...prev,
                                has_end_date: e.target.checked,
                                end_date: e.target.checked ? prev.end_date : ''
                              }));
                            }}
                            className="w-4 h-4 text-primary-500 border-gray-300 rounded focus:ring-primary-500"
                          />
                          <label htmlFor="hasEndDate" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Bitiş Tarihi Belirle
                          </label>
                        </div>
                        
                        {newProject.has_end_date && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Bitiş Tarihi
                            </label>
                            <input
                              type="date"
                              value={newProject.end_date}
                              onChange={(e) => setNewProject({ ...newProject, end_date: e.target.value })}
                              min={newProject.start_date || undefined}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Kategoriler */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Kategoriler
                      </label>
                      {loadingStates.categories ? (
                        <div className="flex items-center justify-center p-4">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-40 overflow-y-auto p-2 border border-gray-200 dark:border-gray-700 rounded-lg">
                          {categories.map(category => (
                            <button
                              key={category.id}
                              onClick={() => {
                                setNewProject(prev => ({
                                  ...prev,
                                  categories: prev.categories.includes(category.id)
                                    ? prev.categories.filter(id => id !== category.id)
                                    : [...prev.categories, category.id]
                                }));
                              }}
                              className={`flex items-center gap-2 p-2 rounded-lg transition-colors ${
                                newProject.categories.includes(category.id)
                                  ? `bg-${category.color}-50 text-${category.color}-700 dark:bg-${category.color}-900/20 dark:text-${category.color}-400 ring-1 ring-${category.color}-500`
                                  : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                              }`}
                            >
                              <div className={`w-3 h-3 rounded-full bg-${category.color}-500`} />
                              <span className="text-sm">{category.name}</span>
                              {newProject.categories.includes(category.id) && (
                                <CheckCircle2 className="w-4 h-4 ml-auto" />
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Alt Projeler */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Alt Projeler
                      </label>
                      {loadingStates.subProjects ? (
                        <div className="flex items-center justify-center p-4">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
                        </div>
                      ) : (
                        <div className="space-y-2 max-h-40 overflow-y-auto p-2 border border-gray-200 dark:border-gray-700 rounded-lg">
                          {subProjects.all?.map(subProject => (
                            <button
                              key={subProject.id}
                              onClick={() => {
                                setNewProject(prev => ({
                                  ...prev,
                                  sub_projects: prev.sub_projects.includes(subProject.id)
                                    ? prev.sub_projects.filter(id => id !== subProject.id)
                                    : [...prev.sub_projects, subProject.id]
                                }));
                              }}
                              className={`w-full flex items-center justify-between p-2 rounded-lg transition-colors ${
                                newProject.sub_projects.includes(subProject.id)
                                  ? 'bg-primary-50 dark:bg-primary-900/20 ring-1 ring-primary-500'
                                  : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium">{subProject.name}</span>
                                <span className={`px-2 py-0.5 text-xs rounded-full ${
                                  subProject.status === 'active'
                                    ? 'bg-green-50 text-green-700 dark:bg-green-900/20'
                                    : subProject.status === 'completed'
                                    ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/20'
                                    : 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20'
                                }`}>
                                  {subProject.status === 'active' ? 'Aktif' : subProject.status === 'completed' ? 'Tamamlandı' : 'Beklemede'}
                                </span>
                              </div>
                              {newProject.sub_projects.includes(subProject.id) && (
                                <CheckCircle2 className="w-4 h-4 text-primary-500" />
                              )}
                            </button>
                          ))}
                          {(!subProjects.all || subProjects.all.length === 0) && (
                            <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                              Kullanılabilir alt proje bulunamadı
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="flex justify-end gap-3 mt-6">
                      <button
                        onClick={handleCreateProject}
                        disabled={loadingStates.creation}
                        className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loadingStates.creation ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            <span>Oluşturuluyor...</span>
                          </>
                        ) : (
                          <span>Proje Oluştur</span>
                        )}
                      </button>
                    </div>
                  </div>
                ) : selectedProjectType === 'subproject' ? (
                  // Alt Proje Oluşturma Formu
                  <div className="space-y-4">
                    {/* Proje Seçimi */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Proje Seçin *
                      </label>
                      <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto">
                        {projects.map((project) => (
                          <button
                            key={project.id}
                            onClick={() => setNewSubProject({
                              ...newSubProject,
                              parent_project_id: project.id
                            })}
                            className={`w-full p-3 text-left border rounded-lg transition-colors ${
                              newSubProject.parent_project_id === project.id
                                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                                : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-gray-900 dark:text-white">
                                {project.name}
                              </span>
                              {newSubProject.parent_project_id === project.id && (
                                <CheckCircle2 className="w-5 h-5 text-primary-500" />
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Alt Proje Adı */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Proje Adı *
                      </label>
                      <input
                        type="text"
                        value={newSubProject.name}
                        onChange={(e) => setNewSubProject({ ...newSubProject, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                        placeholder="Alt proje adını girin"
                      />
                    </div>

                    {/* Alt Proje Açıklaması */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Alt Proje Açıklaması
                      </label>
                      <textarea
                        value={newSubProject.description}
                        onChange={(e) => setNewSubProject({ ...newSubProject, description: e.target.value })}
                        rows="3"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                        placeholder="Alt proje açıklamasını girin (opsiyonel)"
                      />
                    </div>
                    
                    {/* Tarih Alanları */}
                    <div className="space-y-4">
                      {/* Başlangıç Tarihi */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Başlangıç Tarihi
                        </label>
                        <input
                          type="date"
                          name="start_date"
                          value={newSubProject.start_date}
                          onChange={handleDateChange}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      
                      {/* Bitiş Tarihi Kontrolü */}
                      <div className="flex items-center mb-2">
                        <input 
                          type="checkbox" 
                          id="has-end-date"
                          checked={newSubProject.has_end_date}
                          onChange={(e) => setNewSubProject({
                            ...newSubProject,
                            has_end_date: e.target.checked,
                            // Eğer bitiş tarihini belirle seçeneği kapatılırsa, bitiş tarihini temizle
                            end_date: e.target.checked ? newSubProject.end_date : ''
                          })}
                          className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <label htmlFor="has-end-date" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                          Bitiş Tarihi Belirle
                        </label>
                      </div>
                      
                      {/* Bitiş Tarihi (Koşullu Gösterim) */}
                      {newSubProject.has_end_date && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Bitiş Tarihi
                          </label>
                          <input
                            type="date"
                            name="end_date"
                            value={newSubProject.end_date}
                            onChange={handleDateChange}
                            min={newSubProject.start_date || undefined}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                          />
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Bitiş tarihi, başlangıç tarihinden sonra olmalıdır
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="flex justify-end gap-3 mt-6">
                      <button
                        onClick={handleCreateSubProject}
                        disabled={loadingStates.creation}
                        className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loadingStates.creation ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            <span>Oluşturuluyor...</span>
                          </>
                        ) : (
                          <span>Alt Proje Oluştur</span>
                        )}
                      </button>
                    </div>
                  </div>
                ) : (
                  // Kategori Yönetimi Formu
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-lg font-medium text-gray-900 dark:text-white">
                        Proje Kategorileri
                      </h4>
                      <button
                        onClick={() => setIsNewCategoryModalOpen(true)}
                        className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-primary-500 text-white rounded-lg hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <Plus className="w-4 h-4" />
                        <span>Yeni Kategori</span>
                      </button>
                    </div>

                    {/* Kategori Listesi */}
                    <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
                      {loadingStates.categories ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                        </div>
                      ) : categories.length > 0 ? (
                        categories.map(category => (
                          <div key={category.id} className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary-500 dark:hover:border-primary-500 transition-colors">
                            <div className="flex items-center gap-3">
                              <div className="w-4 h-4 rounded-full" style={{ backgroundColor: category.color }}></div>
                              <span className="text-gray-900 dark:text-white">{category.name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => setEditingCategory(category)}
                                className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteCategory(category.id)}
                                className="p-1 text-red-500 hover:text-red-700"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8">
                          <Tags className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                          <p className="text-gray-500 dark:text-gray-400">
                            Henüz hiç kategori oluşturulmamış
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="mt-6 text-sm text-gray-500 dark:text-gray-400">
                      <p>* Kategoriler projeleri gruplandırmak ve filtrelemek için kullanılır.</p>
                      <p>* Her kategoriye özel bir renk atayabilirsiniz.</p>
                      <p>* Silinen kategoriler, bu kategoriye sahip projelerde görünmez olacaktır.</p>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Alt Proje Düzenleme Modal */}
      <AnimatePresence>
        {isSubProjectModalOpen && selectedProjectForModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setIsSubProjectModalOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="relative w-[90vw] max-w-3xl max-h-[85vh] bg-white dark:bg-gray-800 rounded-xl shadow-xl overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                      Alt Projeleri Düzenle
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {selectedSubProjects.length} alt proje seçildi
                    </p>
                  </div>
                  <button
                    onClick={() => setIsSubProjectModalOpen(false)}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Arama ve Filtreleme */}
                  <div className="flex items-center gap-4">
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        value={subProjectSearch}
                        onChange={(e) => setSubProjectSearch(e.target.value)}
                        placeholder="Alt proje ara..."
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div className="min-w-[200px]">
                      <select
                        value={subProjectSort}
                        onChange={(e) => setSubProjectSort(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="name-asc">İsim (A-Z)</option>
                        <option value="name-desc">İsim (Z-A)</option>
                        <option value="status">Durum</option>
                        <option value="team-size">Ekip Büyüklüğü</option>
                      </select>
                    </div>
                  </div>

                  {/* Alt Projeler Listesi */}
                  <div className="grid grid-cols-1 gap-2 max-h-[calc(85vh-250px)] overflow-y-auto pr-2">
                    {(() => {
                      const filteredSubProjects = getFilteredAndSortedSubProjects(subProjects.all || [], subProjectSearch, subProjectSort);
                      
                      if (subProjectSearch && filteredSubProjects.length === 0) {
                        return (
                          <div className="text-center py-8">
                            <Search className="w-12 h-12 mx-auto text-gray-400 mb-3 animate-pulse" />
                            <p className="text-gray-500 dark:text-gray-400 animate-pulse">
                              "{subProjectSearch}" ile eşleşen alt proje bulunamadı
                            </p>
                          </div>
                        );
                      }

                      return filteredSubProjects.map((subProject) => (
                        <button
                          key={subProject.id}
                          onClick={() => {
                            if (selectedSubProjects.includes(subProject.id)) {
                              const updatedSelection = selectedSubProjects.filter(id => id !== subProject.id);
                              // console.log(`Removing subproject ${subProject.id}, new selection:`, updatedSelection);
                              setSelectedSubProjects(updatedSelection);
                            } else {
                              const updatedSelection = [...selectedSubProjects, subProject.id];
                              // console.log(`Adding subproject ${subProject.id}, new selection:`, updatedSelection);
                              setSelectedSubProjects(updatedSelection);
                            }
                          }}
                          className={`w-full p-4 text-left border rounded-lg transition-all ${
                            selectedSubProjects.includes(subProject.id)
                              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 ring-1 ring-primary-500'
                              : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-gray-900 dark:text-white">
                                  {subProject.name}
                                </span>
                                <div
                                  className={`px-2 py-0.5 text-xs rounded-full ${
                                    subProject.status === 'active'
                                      ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                                      : subProject.status === 'completed'
                                      ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
                                      : 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400'
                                  }`}
                                >
                                  {subProject.status === 'active'
                                    ? 'Aktif'
                                    : subProject.status === 'completed'
                                    ? 'Tamamlandı'
                                    : 'Beklemede'}
                                </div>
                              </div>
                              {subProject.description && (
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                                  {subProject.description}
                                </p>
                              )}
                              <div className="flex items-center gap-4 mt-2 text-sm text-gray-500 dark:text-gray-400">
                                <div className="flex items-center gap-1">
                                  <Users2 className="w-4 h-4" />
                                  <span>{subProject.team_size || 0} Üye</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <BarChart2 className="w-4 h-4" />
                                  <span>{subProject.task_count || 0} Görev</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <CheckCircle2 className="w-4 h-4" />
                                  <span>{subProject.completed_tasks || 0} Tamamlanan</span>
                                </div>
                              </div>
                            </div>
                            {selectedSubProjects.includes(subProject.id) && (
                              <CheckCircle2 className="w-5 h-5 text-primary-500 flex-shrink-0 ml-4" />
                            )}
                          </div>
                        </button>
                      ));
                    })()}
                  </div>

                  {/* Alt Bilgi ve Butonlar */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700 ">
                    <div className="text-sm text-gray-500 dark:text-gray-400 ">
                      {subProjectSearch ? (
                        <>
                          Aranan "{subProjectSearch}" için {getFilteredAndSortedSubProjects(subProjects.all || [], subProjectSearch, subProjectSort).length} sonuç bulundu
                        </>
                      ) : (
                        <>Toplam <strong>{subProjects.all?.length || 0}</strong> alt proje mevcut</>
                      )}
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={() => setIsSubProjectModalOpen(false)}
                        className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      >
                        İptal
                      </button>
                      <button
                        onClick={async () => {
                          // console.log(`Saving changes to project ${selectedProjectForModal.id}`);
                          // console.log(`Final selected sub-projects:`, selectedSubProjects);
                          await handleUpdateSubProjects(selectedProjectForModal.id, selectedSubProjects);
                          setIsSubProjectModalOpen(false);
                        }}
                        disabled={loadingStates.subProjects}
                        className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                      >
                        {loadingStates.subProjects ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            <span>Kaydediliyor...</span>
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="w-4 h-4" />
                            <span>Değişiklikleri Kaydet</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Yeni Kategori Oluşturma Modal */}
      <AnimatePresence>
        {isNewCategoryModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setIsNewCategoryModalOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="relative w-[90vw] max-w-2xl max-h-[80vh] bg-white dark:bg-gray-800 rounded-xl shadow-xl overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <button
                    onClick={() => setIsNewCategoryModalOpen(false)}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  </button>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Yeni Kategori Oluştur
                  </h3>
                  <button
                    onClick={handleCreateCategory}
                    className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Kategori Oluştur</span>
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Kategori Adı *
                    </label>
                    <input
                      type="text"
                      value={newCategory.name}
                      onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                      placeholder="Kategori adını girin"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Kategori Rengi
                    </label>
                    <div className="grid grid-cols-7 gap-2">
                      {[
                        { name: 'pink', color: 'bg-pink-500' },
                        { name: 'cyan', color: 'bg-cyan-500' },
                        { name: 'yellow', color: 'bg-yellow-500' },
                        { name: 'gray', color: 'bg-gray-500' },
                        { name: 'emerald', color: 'bg-emerald-500' },
                        { name: 'red', color: 'bg-red-500' },
                        { name: 'amber', color: 'bg-amber-500' },
                        { name: 'slate', color: 'bg-slate-500' },
                        { name: 'orange', color: 'bg-orange-500' },
                        { name: 'blue', color: 'bg-blue-500' },
                        { name: 'purple', color: 'bg-purple-500' },
                        { name: 'indigo', color: 'bg-indigo-500' },
                        { name: 'green', color: 'bg-green-500' },
                        { name: 'white', color: 'bg-white' }
                      ].map((colorOption) => (
                    <button
                          key={colorOption.name}
                          onClick={() => setNewCategory({ ...newCategory, color: colorOption.name })}
                          className={`w-8 h-8 rounded-full ${colorOption.color} transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                            newCategory.color === colorOption.name ? 'ring-2 ring-offset-2 ring-primary-500' : ''
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Proje Düzenleme Modal */}
      <AnimatePresence>
        {isEditModalOpen && editingProject && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => {
                setIsEditModalOpen(false);
                setEditingProject(null);
              }}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="relative w-[90vw] max-w-2xl max-h-[80vh] bg-white dark:bg-gray-800 rounded-xl shadow-xl overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Proje Düzenle
                  </h3>
                  <button
                    onClick={() => {
                      setIsEditModalOpen(false);
                      setEditingProject(null);
                    }}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Proje Adı */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Proje Adı *
                    </label>
                    <input
                      type="text"
                      value={editingProject.name}
                      onChange={(e) => setEditingProject({ ...editingProject, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                      placeholder="Proje adını girin"
                    />
                  </div>

                  {/* Proje Açıklaması */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Proje Açıklaması
                    </label>
                    <textarea
                      value={editingProject.description || ''}
                      onChange={(e) => setEditingProject({ ...editingProject, description: e.target.value })}
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                      placeholder="Proje açıklamasını girin"
                    />
                  </div>

                  {/* Durum */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Proje Durumu
                    </label>
                    <select
                      value={editingProject.status}
                      onChange={(e) => setEditingProject({ ...editingProject, status: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="active">Aktif</option>
                      <option value="completed">Tamamlandı</option>
                      <option value="pending">Beklemede</option>
                    </select>
                  </div>

                  {/* Tarihler */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Başlangıç Tarihi *
                      </label>
                      <input
                        type="date"
                        value={editingProject.start_date}
                        onChange={(e) => {
                          const newStartDate = e.target.value;
                          setEditingProject(prev => ({
                            ...prev,
                            start_date: newStartDate,
                            end_date: prev.end_date && newStartDate > prev.end_date ? '' : prev.end_date
                          }));
                        }}
                        max={editingProject.end_date || undefined}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="editHasEndDate"
                          checked={editingProject.has_end_date}
                          onChange={(e) => {
                            setEditingProject(prev => ({
                              ...prev,
                              has_end_date: e.target.checked,
                              end_date: e.target.checked ? prev.end_date : ''
                            }));
                          }}
                          className="w-4 h-4 text-primary-500 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <label htmlFor="editHasEndDate" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Bitiş Tarihi Belirle
                        </label>
                      </div>
                      
                      {editingProject.has_end_date && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Bitiş Tarihi
                          </label>
                          <input
                            type="date"
                            value={editingProject.end_date || ''}
                            onChange={(e) => setEditingProject({ ...editingProject, end_date: e.target.value })}
                            min={editingProject.start_date || undefined}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 mt-6">
                    <button
                      onClick={() => {
                        setIsEditModalOpen(false);
                        setEditingProject(null);
                      }}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                    >
                      İptal
                    </button>
                    <button
                      onClick={handleEditProject}
                      disabled={loadingStates.creation}
                      className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {loadingStates.creation ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>Güncelleniyor...</span>
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="w-4 h-4" />
                          <span>Güncelle</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Proje Silme Onay Modal */}
      <AnimatePresence>
        {isDeleteModalOpen && projectToDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => {
                setIsDeleteModalOpen(false);
                setProjectToDelete(null);
              }}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="relative w-[90vw] max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-xl"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Proje Sil
                  </h3>
                  <button
                    onClick={() => {
                      setIsDeleteModalOpen(false);
                      setProjectToDelete(null);
                    }}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>

                <div className="mb-6">
                  <p className="text-gray-600 dark:text-gray-400 mb-2">
                    <strong>"{projectToDelete.name}"</strong> projesini silmek istediğinizden emin misiniz?
                  </p>
                  <p className="text-sm text-red-600 dark:text-red-400">
                    Bu işlem geri alınamaz ve projeye ait tüm veriler silinecektir.
                  </p>
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setIsDeleteModalOpen(false);
                      setProjectToDelete(null);
                    }}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                  >
                    İptal
                  </button>
                  <button
                    onClick={handleDeleteProject}
                    disabled={loadingStates.creation}
                    className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {loadingStates.creation ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Siliniyor...</span>
                      </>
                    ) : (
                      <>
                        <X className="w-4 h-4" />
                        <span>Sil</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Projects;
